/**
 * Shared semantics for verified post-procedure outcomes on Home.
 *
 * The surrounding Home shell can remain shared, but the procedure language,
 * modality, and result route must come from the cycle context. In particular,
 * FET must not silently inherit fresh-IVF copy and OI must not inherit IUI
 * terminology.
 */

export type PostProcedureOutcomeModality =
  "fresh_ivf" | "icsi" | "fet" | "iui" | "oi";

export type PostProcedureOutcomeProcedure =
  "embryo_transfer" | "insemination" | "ovulation_window";

export type PostProcedureOutcomeContentKey = "ivf" | "fet" | "iui" | "oi";

export type PostProcedureOutcomeResultStatus =
  "verified-positive" | "verified-negative" | "uncertain" | "adverse" | "unavailable";

export type PostProcedureOutcomeRoute =
  | "ivf-positive"
  | "ivf-negative"
  | "ivf-uncertain"
  | "ivf-adverse"
  | "ivf-unavailable"
  | "fet-positive"
  | "fet-negative"
  | "fet-uncertain"
  | "fet-adverse"
  | "fet-unavailable"
  | "iui-positive"
  | "iui-negative"
  | "iui-uncertain"
  | "iui-adverse"
  | "iui-unavailable"
  | "oi-positive"
  | "oi-negative"
  | "oi-uncertain"
  | "oi-adverse"
  | "oi-unavailable";

export type PostProcedureOutcomeContext = {
  modality: PostProcedureOutcomeModality;
  procedure: PostProcedureOutcomeProcedure;
  contentKey: PostProcedureOutcomeContentKey;
  resultStatus: PostProcedureOutcomeResultStatus;
  route: PostProcedureOutcomeRoute;
};

function resolveModality(
  cycleType: string | null | undefined,
): Pick<PostProcedureOutcomeContext, "modality" | "procedure" | "contentKey"> {
  if (cycleType === "fet") {
    return {
      modality: "fet",
      procedure: "embryo_transfer",
      contentKey: "fet",
    };
  }

  if (cycleType === "iui") {
    return {
      modality: "iui",
      procedure: "insemination",
      contentKey: "iui",
    };
  }

  if (cycleType === "oi") {
    return {
      modality: "oi",
      procedure: "ovulation_window",
      contentKey: "oi",
    };
  }

  if (cycleType === "icsi") {
    return {
      modality: "icsi",
      procedure: "embryo_transfer",
      contentKey: "ivf",
    };
  }

  return {
    modality: "fresh_ivf",
    procedure: "embryo_transfer",
    contentKey: "ivf",
  };
}

export function getPostProcedureOutcomeContext(
  cycleType: string | null | undefined,
  resultStatus: PostProcedureOutcomeResultStatus,
): PostProcedureOutcomeContext {
  const modality = resolveModality(cycleType);
  return {
    ...modality,
    resultStatus,
    route: `${modality.contentKey}-${
      resultStatus === "verified-positive"
        ? "positive"
        : resultStatus === "verified-negative"
          ? "negative"
          : resultStatus === "uncertain"
            ? "uncertain"
            : resultStatus === "adverse"
              ? "adverse"
              : "unavailable"
    }` as PostProcedureOutcomeRoute,
  };
}
